/**
 * bg-removal-tool.js
 * Ashe Design – storefront background removal tool (the upload card).
 *
 * 1. The customer picks or drops a photo (JPG, PNG, WebP, iPhone HEIC, ...)
 * 2. The photo is turned into a PNG inside the browser (the server only takes PNG)
 *    - iPhone HEIC: Safari opens it by itself. Other browsers load the
 *      heic-to library, but only when a HEIC photo is actually picked.
 *    - Very large photos are scaled down until they fit the server's limit.
 * 3. The PNG goes to POST /remove-background with surface "storefront"
 * 4. The card shows Download + "Remove another", the before/after slider
 *    shows the customer's own photo and result, and the credit count refreshes
 *
 * Works with the other page scripts through events on [data-bgr-root]:
 *   listens for  "bgr:account"          (latest account data)
 *   sends        "bgr:credits-changed"  (a credit was just spent)
 *   sends        "bgr:result"           (show this photo + result in the slider)
 *   sends        "bgr:reset"            (put the example photos back)
 */
(() => {
  'use strict';

  // ---------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------
  const MAX_UPLOAD_BYTES = 14 * 1024 * 1024; // server limit is 15 MiB; keep some room
  const MAX_INPUT_BYTES = 80 * 1024 * 1024; // refuse huge files before trying to open them
  // iPhone Safari can't draw canvases bigger than about 16.7 million pixels.
  const MAX_CANVAS_PIXELS = 16000000;
  const MAX_SHRINK_ATTEMPTS = 5;
  const REQUEST_TIMEOUT_MS = 120000;
  const PLANS_ANCHOR = '#bgr-plans';

  // Pinned version + integrity hash: the browser refuses the file if even
  // one byte differs from the version that was checked.
  const HEIC_LIBRARY = {
    src: 'https://cdn.jsdelivr.net/npm/heic-to@1.5.2/dist/iife/heic-to.js',
    integrity: 'sha384-cVm8gaWQ5+URpoh6ACKXpm8TuyoHkfIDDBkxvDoUdIZ18w8nV5en0lVQvWMwO/6S',
  };

  const MESSAGES = {
    not_authorized: 'Your login has expired. Please log in again.',
    no_credits: "You're out of credits. Pick a plan or a 1-Time Purchase to get more.",
    revoked: "This account can't use background removal. Please contact us for help.",
    too_big: 'This photo is too large. Please try a smaller one.',
    unreadable: "We couldn't open this file. Please try a JPG, PNG, or iPhone photo.",
    heic_library: "We couldn't open this iPhone photo. Check your internet connection and try again.",
    rejected: "We couldn't remove the background from this image. Please try a different photo. No credit was used.",
    service: 'Our background remover is busy right now. Please try again in a minute. No credit was used.',
    timeout: 'This is taking too long. Please try again. No credit was used.',
    network: "We couldn't reach the server. Check your internet connection and try again.",
    browser: "Your browser couldn't prepare this photo. Please try a smaller photo or a different browser.",
    bad_reply: 'Something went wrong. Please try again.',
    unknown: 'Something went wrong. Please try again in a moment.',
  };

  // ---------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------
  class ToolError extends Error {
    constructor(code, message) {
      super(message || code);
      this.name = 'ToolError';
      this.code = code;
    }
  }

  function plural(n, word) {
    const count = Number(n) || 0;
    return `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;
  }

  function readConfig(root) {
    const node = root.querySelector('[data-bgr-config]');
    if (!node) throw new Error('Config block is missing');
    const raw = JSON.parse(node.textContent);
    return {
      apiBase: String(raw.apiBase || '').replace(/\/+$/, ''),
      key: String(raw.key || ''),
      loginUrl: String(raw.loginUrl || '/account/login'),
    };
  }

  // "Family Photo.HEIC" -> "Family Photo-no-background.png"
  function downloadName(originalName) {
    const base = String(originalName || '')
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w\- ]+/g, '')
      .trim();
    return `${base || 'image'}-no-background.png`;
  }

  // ---------------------------------------------------------------
  // HEIC support (loaded only when needed)
  // ---------------------------------------------------------------
  function looksLikeHeic(file) {
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    return type.startsWith('image/heic') || type.startsWith('image/heif') || /\.(heic|heif)$/.test(name);
  }

  let heicLibraryPromise = null;

  function loadHeicLibrary() {
    if (typeof window.HeicTo === 'function') return Promise.resolve(window.HeicTo);
    if (heicLibraryPromise) return heicLibraryPromise;

    heicLibraryPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = HEIC_LIBRARY.src;
      script.integrity = HEIC_LIBRARY.integrity;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => {
        if (typeof window.HeicTo === 'function') resolve(window.HeicTo);
        else reject(new ToolError('heic_library', 'Library loaded but HeicTo is missing'));
      };
      script.onerror = () => reject(new ToolError('heic_library', 'Library failed to load'));
      document.head.appendChild(script);
    }).catch((error) => {
      heicLibraryPromise = null; // allow a retry later
      throw error;
    });

    return heicLibraryPromise;
  }

  async function decodeHeic(file) {
    const heicTo = await loadHeicLibrary();
    let isHeic = false;
    try {
      isHeic = await heicTo.isHeic(file);
    } catch (error) {
      console.warn('[bg-removal-tool] HEIC check failed:', error);
    }
    if (!isHeic) throw new ToolError('unreadable');
    try {
      return await heicTo({ blob: file, type: 'bitmap' });
    } catch (error) {
      console.error('[bg-removal-tool] HEIC conversion failed:', error);
      throw new ToolError('unreadable');
    }
  }

  // ---------------------------------------------------------------
  // Opening a photo and turning it into a PNG that fits
  // ---------------------------------------------------------------
  function decodeWithImgElement(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new ToolError('unreadable'));
      };
      img.src = url;
    });
  }

  // Uses the photo's rotation info, so sideways phone photos come out upright.
  async function decodeNatively(blob) {
    if (typeof window.createImageBitmap === 'function') {
      try {
        return await createImageBitmap(blob, { imageOrientation: 'from-image' });
      } catch {
        // Older browsers: fall back to an <img> element below.
      }
    }
    return decodeWithImgElement(blob);
  }

  async function decodeImage(file, onStatus) {
    try {
      return await decodeNatively(file);
    } catch (nativeError) {
      // Only try the HEIC library for files that could be HEIC.
      // (Windows often gives HEIC files no type at all.)
      if (!looksLikeHeic(file) && file.type) throw nativeError;
    }
    onStatus('Opening your iPhone photo…');
    return decodeHeic(file);
  }

  function sizeOf(source) {
    return {
      width: source.naturalWidth || source.width,
      height: source.naturalHeight || source.height,
    };
  }

  function drawToPng(source, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ToolError('browser', 'No 2D canvas');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, width, height);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        // Free the canvas memory right away (matters on iPhones).
        canvas.width = 1;
        canvas.height = 1;
        if (blob) resolve(blob);
        else reject(new ToolError('browser', 'canvas.toBlob returned nothing'));
      }, 'image/png');
    });
  }

  async function makePngThatFits(source, onStatus) {
    const { width: fullWidth, height: fullHeight } = sizeOf(source);
    if (!fullWidth || !fullHeight) throw new ToolError('unreadable');

    let scale = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (fullWidth * fullHeight)));

    for (let attempt = 1; attempt <= MAX_SHRINK_ATTEMPTS; attempt += 1) {
      const width = Math.max(1, Math.round(fullWidth * scale));
      const height = Math.max(1, Math.round(fullHeight * scale));
      const blob = await drawToPng(source, width, height);
      if (blob.size <= MAX_UPLOAD_BYTES) return { blob, resized: scale < 1 };

      // Too big: shrink by about the right amount and try again.
      onStatus('Making your photo a little smaller…');
      scale *= Math.sqrt(MAX_UPLOAD_BYTES / blob.size) * 0.95;
    }
    throw new ToolError('too_big');
  }

  async function prepareImage(file, onStatus) {
    if (file.size > MAX_INPUT_BYTES) throw new ToolError('too_big');

    // A PNG that already fits is sent exactly as it is.
    if (file.type === 'image/png' && file.size <= MAX_UPLOAD_BYTES) {
      return { blob: file, resized: false };
    }

    onStatus('Getting your photo ready…');
    const source = await decodeImage(file, onStatus);
    try {
      return await makePngThatFits(source, onStatus);
    } finally {
      if (typeof source.close === 'function') source.close(); // free ImageBitmap memory
    }
  }

  // ---------------------------------------------------------------
  // Talking to the server
  // ---------------------------------------------------------------
  function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new ToolError('browser', 'Could not read the image'));
      reader.readAsDataURL(blob); // "data:image/png;base64,..." (the server accepts this)
    });
  }

  // 401 has no details.code, and 402 uses "error": "no_credits", so the
  // status number decides most cases.
  function errorCode(status, data) {
    if (status === 401) return 'not_authorized';
    if (status === 402) return 'no_credits';
    const detail = data && data.details && data.details.code;
    if (detail && MESSAGES[detail]) return detail;
    if (status === 403) return 'revoked';
    if (status === 413) return 'too_big';
    if (status === 400) return 'rejected';
    if (status === 502 || status === 503) return 'service';
    if (status === 504) return 'timeout';
    return 'unknown';
  }

  async function removeBackground(config, pngBlob) {
    const image = await blobToDataUrl(pngBlob);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      let response;
      try {
        response = await fetch(`${config.apiBase}/remove-background`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${config.key}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ image, surface: 'storefront' }),
          cache: 'no-store',
          signal: controller.signal,
        });
      } catch (error) {
        throw new ToolError(error.name === 'AbortError' ? 'timeout' : 'network', error.message);
      }

      if (!response.ok) {
        let data = null;
        try {
          data = await response.json();
        } catch {
          // Reply wasn't JSON.
        }
        throw new ToolError(errorCode(response.status, data), `HTTP ${response.status}`);
      }

      const type = response.headers.get('Content-Type') || '';
      if (!type.includes('image/png')) throw new ToolError('bad_reply', `Unexpected type: ${type}`);

      const result = await response.blob();
      const left = Number.parseInt(response.headers.get('X-Credits-Remaining'), 10);
      return { blob: result, creditsLeft: Number.isFinite(left) ? left : null };
    } catch (error) {
      if (error instanceof ToolError) throw error;
      throw new ToolError(error && error.name === 'AbortError' ? 'timeout' : 'network', error && error.message);
    } finally {
      clearTimeout(timer);
    }
  }

  // ---------------------------------------------------------------
  // View: everything that touches the upload card
  // ---------------------------------------------------------------
  function createToolView(tool) {
    const find = (name) => tool.querySelector(`[data-bgr-${name}]`);
    const nodes = {
      drop: find('drop'),
      choose: find('choose'),
      fileInput: find('file'),
      progress: find('progress'),
      progressText: find('progress-text'),
      result: find('result'),
      resultNote: find('result-note'),
      download: find('download'),
      again: find('again'),
      error: find('tool-error'),
    };

    function show(node, visible) {
      if (node) node.hidden = !visible;
    }

    return {
      nodes,

      showProgress(text) {
        show(nodes.error, false);
        show(nodes.result, false);
        show(nodes.drop, false);
        show(nodes.progress, true);
        if (nodes.progressText) nodes.progressText.textContent = text;
      },

      showResult(url, fileName, creditsLeft, resized) {
        show(nodes.progress, false);
        show(nodes.drop, false);
        if (nodes.download) {
          nodes.download.href = url;
          nodes.download.download = fileName;
        }
        const notes = [];
        if (creditsLeft !== null) notes.push(`1 credit used. ${plural(creditsLeft, 'credit')} left.`);
        if (resized) notes.push('We made your photo a little smaller so it could be processed.');
        if (nodes.resultNote) nodes.resultNote.textContent = notes.join(' ');
        show(nodes.result, true);
      },

      showError(message, link = null) {
        show(nodes.progress, false);
        show(nodes.result, false);
        show(nodes.drop, true);
        if (!nodes.error) return;
        nodes.error.replaceChildren(document.createTextNode(message));
        if (link) {
          const anchor = document.createElement('a');
          anchor.href = link.href;
          anchor.textContent = link.text;
          nodes.error.append(' ', anchor);
        }
        show(nodes.error, true);
      },

      reset() {
        show(nodes.progress, false);
        show(nodes.result, false);
        show(nodes.error, false);
        show(nodes.drop, true);
        if (nodes.download) nodes.download.removeAttribute('href');
      },

      setDragging(dragging) {
        if (nodes.drop) nodes.drop.classList.toggle('is-dragging', dragging);
      },
    };
  }

  // ---------------------------------------------------------------
  // Controller
  // ---------------------------------------------------------------
  function initTool(root) {
    const tool = root.querySelector('[data-bgr-tool]');
    if (!tool || tool.dataset.bgrReady === 'true') return;
    tool.dataset.bgrReady = 'true';

    let config;
    try {
      config = readConfig(root);
    } catch (error) {
      console.error('[bg-removal-tool] Could not read page settings:', error);
      return;
    }
    if (!config.key) return;

    const view = createToolView(tool);
    const state = {
      busy: false,
      // Browser-only links to the customer's photo and result (object URLs).
      beforeUrl: null,
      resultUrl: null,
      // null = not known yet; the server still checks.
      balance: root.bgrAccount && root.bgrAccount.balance ? root.bgrAccount.balance.total : null,
    };

    root.addEventListener('bgr:account', (event) => {
      const account = event.detail;
      state.balance = account && account.balance ? account.balance.total : null;
    });

    function revoke(url) {
      if (url) URL.revokeObjectURL(url);
    }

    function releaseResult() {
      revoke(state.beforeUrl);
      revoke(state.resultUrl);
      state.beforeUrl = null;
      state.resultUrl = null;
    }

    function linkFor(code) {
      if (code === 'not_authorized') return { href: config.loginUrl, text: 'Log in' };
      if (code === 'no_credits') return { href: PLANS_ANCHOR, text: 'See options' };
      return null;
    }

    async function handleFile(file) {
      if (state.busy || !file) return;
      if (state.balance === 0) {
        view.showError(MESSAGES.no_credits, linkFor('no_credits'));
        return;
      }

      state.busy = true;
      view.showProgress('Getting your photo ready…');

      try {
        const prepared = await prepareImage(file, view.showProgress);
        view.showProgress('Removing the background… this can take up to 30 seconds.');
        const { blob, creditsLeft } = await removeBackground(config, prepared.blob);

        // Swap in the new images first, then free the old ones, so the
        // slider never points at a link that's already gone.
        const oldBefore = state.beforeUrl;
        const oldResult = state.resultUrl;
        state.beforeUrl = URL.createObjectURL(prepared.blob); // always a PNG, so every browser can show it
        state.resultUrl = URL.createObjectURL(blob);
        if (creditsLeft !== null) state.balance = creditsLeft;

        view.showResult(state.resultUrl, downloadName(file.name), creditsLeft, prepared.resized);
        root.dispatchEvent(
          new CustomEvent('bgr:result', { detail: { beforeUrl: state.beforeUrl, afterUrl: state.resultUrl } })
        );
        root.dispatchEvent(new CustomEvent('bgr:credits-changed'));
        revoke(oldBefore);
        revoke(oldResult);
      } catch (error) {
        if (!(error instanceof ToolError)) console.error('[bg-removal-tool]', error);
        const code = error instanceof ToolError ? error.code : 'unknown';
        if (code === 'no_credits') state.balance = 0;
        view.showError(MESSAGES[code] || MESSAGES.unknown, linkFor(code));
      } finally {
        state.busy = false;
      }
    }

    const { nodes } = view;

    // Older page versions used a button that "clicked" the file input.
    // The new page uses a <label for="..."> instead, which needs no code.
    if (nodes.choose && nodes.fileInput) {
      nodes.choose.addEventListener('click', () => {
        if (!state.busy) nodes.fileInput.click();
      });
    }

    if (nodes.fileInput) {
      nodes.fileInput.addEventListener('change', () => {
        const file = nodes.fileInput.files && nodes.fileInput.files[0];
        nodes.fileInput.value = ''; // lets the same file be picked again
        handleFile(file);
      });
    }

    if (nodes.drop) {
      ['dragenter', 'dragover'].forEach((type) =>
        nodes.drop.addEventListener(type, (event) => {
          event.preventDefault();
          view.setDragging(true);
        })
      );
      ['dragleave', 'dragend'].forEach((type) =>
        nodes.drop.addEventListener(type, () => view.setDragging(false))
      );
      nodes.drop.addEventListener('drop', (event) => {
        event.preventDefault();
        view.setDragging(false);
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        handleFile(file);
      });
    }

    if (nodes.again) {
      nodes.again.addEventListener('click', () => {
        if (state.busy) return;
        root.dispatchEvent(new CustomEvent('bgr:reset')); // slider goes back to the examples first
        releaseResult();
        view.reset();
      });
    }
  }

  // ---------------------------------------------------------------
  // Start
  // ---------------------------------------------------------------
  function boot(scope = document) {
    scope.querySelectorAll('[data-bgr-root]').forEach((root) => {
      try {
        initTool(root);
      } catch (error) {
        console.error('[bg-removal-tool] Startup failed:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
})();